import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, QuoteIcon } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Header } from '../components/Header';
import { differentiators, expertiseAreas, sessionBenefits, testimonials } from '../data/drAli';
const heroImage = 'https://cdn.magicpatterns.com/patterns/generated-images/52c17fb8-8f59-4568-b9a2-10661c2aeb04.jpg';
const bookingUrl = 'https://masters.peptidescienceinstitute.com/';
export function Home() {
  const shouldReduceMotion = useReducedMotion();
  return <div className="min-h-screen w-full bg-[#FAFAF9] text-[#0A1628]" data-id="element-17" data-mp="29f27oflbqqh8">
      <Header data-id="element-18" data-mp="2dhgsv6cgu2bo" />
      <main data-id="element-19" data-mp="2dhywwp5ddku5">
        <section id="hero" className="relative flex min-h-screen scroll-mt-20 items-center overflow-hidden bg-[#0A1628] px-5 py-32 sm:px-8" data-id="element-20" data-mp="2dirsctyvonwz">
          <div className="absolute inset-x-0 top-20 h-px bg-white/10" aria-hidden="true" data-id="element-21" data-mp="3owlyoj7w8gzi" />

          <div className="relative mx-auto grid w-full max-w-7xl items-center gap-16 lg:grid-cols-[1.1fr_0.9fr]" data-id="element-22" data-mp="30wjcmpo0ou4v">
            <div data-id="element-23" data-mp="2vx6taewcu571">
              <motion.p initial={shouldReduceMotion ? false : {
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: shouldReduceMotion ? 0 : 0.24,
              ease: [0.23, 1, 0.32, 1]
            }} className="mb-8 text-sm font-medium text-[#D4AF37]" data-id="element-24" data-mp="2vyq8s3a6sc7r">
                Dr. Ali Mazloum · Co-Founder, Peptide Science Institute
              </motion.p>
              <motion.h1 initial={shouldReduceMotion ? false : {
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: shouldReduceMotion ? 0 : 0.28,
              delay: shouldReduceMotion ? 0 : 0.05,
              ease: [0.23, 1, 0.32, 1]
            }} className="font-serif text-6xl font-semibold leading-[0.98] tracking-[-0.045em] text-white sm:text-7xl lg:text-8xl" data-id="element-25" data-mp="2d5w35zygdwg7">
                The Human MRI
              </motion.h1>
              <motion.p initial={shouldReduceMotion ? false : {
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: shouldReduceMotion ? 0 : 0.28,
              delay: shouldReduceMotion ? 0 : 0.1,
              ease: [0.23, 1, 0.32, 1]
            }} className="mt-8 max-w-xl font-serif text-2xl leading-snug text-[#E5C76B] sm:text-3xl" data-id="element-26" data-mp="1kj5ulvds95j9">
                Diagnostic precision for the cases others miss.
              </motion.p>
              <motion.p initial={shouldReduceMotion ? false : {
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: shouldReduceMotion ? 0 : 0.28,
              delay: shouldReduceMotion ? 0 : 0.15,
              ease: [0.23, 1, 0.32, 1]
            }} className="mt-6 max-w-xl text-lg leading-8 text-white/70" data-id="element-27" data-mp="289072bu6ws2z">
                If you've tried the diets, the supplements, and the specialists — and you're still
                not getting real answers — this is a different kind of appointment. One built to
                find what's actually happening in your body, and exactly what to do about it.
              </motion.p>
              <motion.div initial={shouldReduceMotion ? false : {
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: shouldReduceMotion ? 0 : 0.28,
              delay: shouldReduceMotion ? 0 : 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-28" data-mp="ocuc1pp8mrps">
                <a href="#booking" className="mt-10 inline-flex items-center gap-3 bg-[#D4AF37] px-8 py-4 font-semibold text-[#0A1628] transition-[background-color,transform] duration-150 ease-out hover:-translate-y-0.5 hover:bg-[#E5C76B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-[#0A1628]" data-id="element-29" data-mp="3nukhy3a4gqtt">
                  Book your session
                  <ArrowRightIcon className="h-5 w-5" data-id="element-30" data-mp="27eep88ghsxgv" />
                </a>
              </motion.div>
            </div>

            <motion.figure initial={shouldReduceMotion ? false : {
            opacity: 0,
            x: 18
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: shouldReduceMotion ? 0 : 0.28,
            delay: shouldReduceMotion ? 0 : 0.15,
            ease: [0.23, 1, 0.32, 1]
          }} className="relative mx-auto w-full max-w-[27rem] lg:mr-0" data-id="element-31" data-mp="2ce138k45ahqq">
              <div className="mb-4 flex items-center justify-between border-b border-white/15 pb-3 text-[0.68rem] font-medium text-white/50" data-id="element-32" data-mp="2vbkelue2scgb">
                <span data-id="element-33" data-mp="303vdctx2xs7a">Diagnostic imaging</span>
                <span className="flex items-center gap-2 text-[#D4AF37]" data-id="element-34" data-mp="351nfshvcowpq">
                  <span className="h-1.5 w-1.5 bg-[#D4AF37]" aria-hidden="true" data-id="element-35" data-mp="353q50zooilte" />
                  Precision review
                </span>
              </div>

              <div className="absolute -bottom-3 -left-3 h-[calc(100%-3.75rem)] w-full border border-[#D4AF37]/35" style={{
              clipPath: 'polygon(8% 0, 100% 0, 100% 88%, 88% 100%, 0 100%, 0 12%)'
            }} aria-hidden="true" data-id="element-36" data-mp="3e3d7zpx23zok" />
              <div className="relative aspect-[4/5] overflow-hidden bg-[#07101D]" style={{
              clipPath: 'polygon(8% 0, 100% 0, 100% 88%, 88% 100%, 0 100%, 0 12%)'
            }} data-id="element-37" data-mp="tds1xwd7e686">
                <img src={heroImage} alt="Diagnostic imaging workstation displaying detailed MRI scans" className="h-full w-full object-cover object-[50%_58%] opacity-90 saturate-[0.72] contrast-[1.08]" data-id="element-38" data-mp="1m38sspl6df2a" />
                <div className="absolute inset-x-5 bottom-5 border-l-2 border-[#D4AF37] bg-[#0A1628]/90 px-4 py-3 backdrop-blur-md" data-id="element-39" data-mp="17eclwvuuu839">
                  <p className="text-xs font-semibold text-white" data-id="element-40" data-mp="24uic1qkqzi72">Seeing what others miss</p>
                  <p className="mt-1 text-[0.68rem] text-white/55" data-id="element-41" data-mp="202gw9uodf9ca">MRI · EKG · Laboratory analysis</p>
                </div>
              </div>
              <figcaption className="mt-5 max-w-sm text-xs leading-5 text-white/40" data-id="element-42" data-mp="3ucp0dlqof5pw">
                A conceptual representation of the diagnostic precision behind Dr. Ali&apos;s approach.
              </figcaption>
            </motion.figure>
          </div>
        </section>

        <section className="scroll-mt-20 bg-[#0A1628] px-5 pb-24 pt-4 text-white sm:px-8 md:pb-32" data-id="element-43" data-mp="3pikqatm46hkl">
          <div className="mx-auto max-w-7xl" data-id="element-44" data-mp="3al1uj7b0r43n">
            <AnimatedSection className="grid gap-10 border-t border-white/15 pt-14 lg:grid-cols-[0.8fr_1.2fr] lg:items-start" data-id="element-45" data-mp="3floeadx0bdba">
              <h2 className="font-serif text-4xl font-semibold leading-tight tracking-[-0.03em] sm:text-5xl" data-id="element-46" data-mp="31moq17dep0fn">
                You've done the work.
                <br data-id="element-47" data-mp="36n47ob4q0u3f" />
                You still don't have answers.
              </h2>
              <div className="max-w-2xl space-y-5 text-lg leading-8 text-white/65" data-id="element-48" data-mp="zvbnuq2tp6na">
                <p data-id="element-49" data-mp="14t4u1j44m0nk">
                  The diets. The supplements. The specialists who treat one symptom at a time
                  while the rest of your body keeps sending signals no one connects. It isn't a
                  lack of effort. It's that most approaches were never built to see the whole
                  picture.
                </p>
                <p data-id="element-50" data-mp="1izf7ud0f27nu">
                  Dr. Ali treats the body as a single, integrated system. Instead of chasing the
                  next symptom, he looks for the source — and builds a precise plan from there.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </section>

        <section id="expertise" className="scroll-mt-20 bg-[#FAFAF9] px-5 py-24 sm:px-8 md:py-32" data-id="element-51" data-mp="26p9g0h9sj81r">
          <div className="mx-auto max-w-7xl" data-id="element-52" data-mp="22mmjw46370yr">
            <AnimatedSection className="grid gap-10 border-b border-[#0A1628]/15 pb-14 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-53" data-mp="22nxna2vme6pc">
              <div data-id="element-54" data-mp="894yont5mg6w">
                <p className="mb-5 text-sm font-semibold text-[#9B7A16]" data-id="element-55" data-mp="89wsbt36y3uj">Where he's made a difference</p>
                <h2 className="font-serif text-5xl font-semibold leading-tight tracking-[-0.035em] sm:text-6xl" data-id="element-56" data-mp="wuuz9zu56hw1">
                  The cases brought to Dr. Ali
                </h2>
              </div>
              <p className="max-w-2xl text-lg leading-8 text-[#0A1628]/65 lg:justify-self-end" data-id="element-57" data-mp="16jmnbww30i1o">
                Individual results vary and nothing here is a guarantee — but these are the kinds
                of conditions clients have come to him with, and the outcomes they've worked
                toward together.
              </p>
            </AnimatedSection>

            <div className="grid lg:grid-cols-2" data-id="element-58" data-mp="mvvpgdxn4g7g">
              {expertiseAreas.map((area, index) => <AnimatedSection key={area.title} delay={Math.min(index * 0.035, 0.14)} className={`py-9 lg:py-11 ${index % 2 === 0 ? 'lg:pr-12' : 'lg:border-l lg:border-[#0A1628]/15 lg:pl-12'} ${index < expertiseAreas.length - 2 ? 'border-b border-[#0A1628]/15' : ''}`} data-id="element-59" data-mp="ruqis9t8hn5t" data-mpi={index}>
                  <h3 className="font-serif text-2xl font-semibold sm:text-3xl" data-id="element-60" data-mp="2dcr8qn43bty0">{area.title}</h3>
                  <p className="mt-4 max-w-xl leading-7 text-[#0A1628]/65" data-id="element-61" data-mp="28gahhk1lgzgp">{area.description}</p>
                </AnimatedSection>)}
            </div>
          </div>
        </section>

        <section id="approach" className="scroll-mt-20 bg-[#0A1628] px-5 py-24 text-white sm:px-8 md:py-32" data-id="element-62" data-mp="1zlw45n0mjz08">
          <div className="mx-auto max-w-7xl" data-id="element-63" data-mp="1up5ov1wn97fv">
            <AnimatedSection className="max-w-4xl" data-id="element-64" data-mp="1porh7kyyfzid">
              <p className="mb-5 text-sm font-semibold text-[#D4AF37]" data-id="element-65" data-mp="1pmwwa6noo7ea">The approach</p>
              <h2 className="font-serif text-5xl font-semibold leading-tight tracking-[-0.035em] sm:text-6xl lg:text-7xl" data-id="element-66" data-mp="1kq6glkfsjdte">
                Precision before protocol.
              </h2>
              <p className="mt-8 max-w-3xl text-xl leading-9 text-white/65" data-id="element-67" data-mp="3f2eglc6yykw1">
                As co-founder of the Peptide Science Institute, Dr. Ali's distinguishing strength
                is diagnostic precision — reviewing MRI, EKG, and lab work to identify overlooked
                pathologies, then building a strategy around the whole system rather than treating
                the body as disconnected parts.
              </p>
            </AnimatedSection>

            <div className="mt-16 grid border-t border-white/15 md:grid-cols-2" data-id="element-68" data-mp="hnobu1y7vdn9">
              {differentiators.map((item, index) => <AnimatedSection key={item.title} delay={Math.min(index * 0.04, 0.12)} className={`py-10 md:py-12 ${index % 2 === 0 ? 'md:pr-12' : 'md:border-l md:border-white/15 md:pl-12'} ${index < 2 ? 'border-b border-white/15' : ''}`} data-id="element-69" data-mp="3q4j46nhe1fn3" data-mpi={index}>
                  <h3 className="font-serif text-2xl font-semibold text-white" data-id="element-70" data-mp="3v0zv1pfz283v">{item.title}</h3>
                  <p className="mt-4 max-w-xl leading-7 text-white/60" data-id="element-71" data-mp="37boyyl9kh1w2">{item.description}</p>
                </AnimatedSection>)}
            </div>
          </div>
        </section>

        <section id="testimonials" className="scroll-mt-20 bg-[#F2EFE8] px-5 py-24 sm:px-8 md:py-32" data-id="element-72" data-mp="1hv2wzsfstr00">
          <div className="mx-auto max-w-7xl" data-id="element-73" data-mp="1mncubtj8ttcb">
            <AnimatedSection className="grid gap-8 lg:grid-cols-[0.75fr_1.25fr] lg:items-end" data-id="element-74" data-mp="18v6gie3kbyg1">
              <div data-id="element-75" data-mp="1cwrnjitf1iib">
                <p className="mb-5 text-sm font-semibold text-[#9B7A16]" data-id="element-76" data-mp="1cw9u0ty51ie3">Client words</p>
                <h2 className="font-serif text-5xl font-semibold tracking-[-0.035em] sm:text-6xl" data-id="element-77" data-mp="20mlzzz9tp521">
                  In their own words
                </h2>
              </div>
              <p className="max-w-2xl text-lg leading-8 text-[#0A1628]/65 lg:justify-self-end" data-id="element-78" data-mp="25jcn36k1p0wy">
                Individual experiences shared by clients who worked with Dr. Ali.
              </p>
            </AnimatedSection>

            <div className="mt-16 grid gap-px overflow-hidden border border-[#0A1628]/15 bg-[#0A1628]/15 md:grid-cols-2" data-id="element-79" data-mp="g6sfdph6gm09">
              {testimonials.map((testimonial, index) => <AnimatedSection key={testimonial.author} delay={Math.min(index * 0.04, 0.12)} className="flex min-h-[22rem] flex-col bg-[#FAFAF9] p-8 sm:p-10" data-id="element-80" data-mp="2murqsg69zt4d" data-mpi={index}>
                  <QuoteIcon className="h-8 w-8 text-[#B18A1D]" strokeWidth={1.4} aria-hidden="true" data-id="element-81" data-mp="2d46h9xp2d46o" />
                  <blockquote className="mt-8 flex flex-1 flex-col" data-id="element-82" data-mp="29462gby1yqz3">
                    <p className="font-serif text-2xl leading-9 text-[#0A1628]" data-id="element-83" data-mp="2449xa9dmcthy">“{testimonial.quote}”</p>
                    <footer className="mt-auto pt-10 text-sm font-semibold text-[#0A1628]/60" data-id="element-84" data-mp="1z5gjsgyc12hs">— {testimonial.author}</footer>
                  </blockquote>
                </AnimatedSection>)}
            </div>
          </div>
        </section>

        <section id="booking" className="scroll-mt-20 bg-[#0A1628] px-5 py-24 text-white sm:px-8 md:py-32" data-id="element-85" data-mp="1q6bnzie5nn9b">
          <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[1fr_0.8fr] lg:items-start" data-id="element-86" data-mp="3oi0w6bc5a5kt">
            <AnimatedSection data-id="element-87" data-mp="3kgfbi3tdcn02">
              <p className="mb-5 text-sm font-semibold text-[#D4AF37]" data-id="element-88" data-mp="16b0lhjjip4jj">Book a session</p>
              <h2 className="max-w-3xl font-serif text-5xl font-semibold leading-tight tracking-[-0.035em] sm:text-6xl lg:text-7xl" data-id="element-89" data-mp="1b9vfziqukhjh">
                Clarity in a single, focused session.
              </h2>
              <p className="mt-8 max-w-2xl text-lg leading-8 text-white/65" data-id="element-90" data-mp="1k25n2r6g46wi">
                This is direct access to Dr. Ali's analysis and a clear roadmap for what to do
                next — without requiring enrollment in a longer program.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={0.08} className="border border-white/15 bg-white/[0.04] p-7 sm:p-10" data-id="element-91" data-mp="wcktleco1r8z">
              <p className="font-serif text-5xl font-semibold text-white" data-id="element-92" data-mp="11bfnbbc6907v">$1,500</p>
              <p className="mt-2 text-sm text-white/55" data-id="element-93" data-mp="119bb8644cuwi">One focused session with Dr. Ali</p>
              <ul className="mt-9 space-y-5" data-id="element-94" data-mp="27tufs8n6ixdz">
                {sessionBenefits.map(benefit => <li key={benefit} className="flex gap-3 text-sm leading-6 text-white/75" data-id="element-95" data-mp="2csnrpwmtigc1">
                    <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-96" data-mp="2bbru3gu1ling" />
                    <span data-id="element-97" data-mp="26cow2gchccoz">{benefit}</span>
                  </li>)}
              </ul>
              <a href={bookingUrl} className="mt-10 inline-flex w-full items-center justify-center gap-3 bg-[#D4AF37] px-7 py-4 text-center font-semibold text-[#0A1628] transition-[background-color,transform] duration-150 ease-out hover:-translate-y-0.5 hover:bg-[#E5C76B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-[#0A1628]" data-id="element-98" data-mp="2k80scs6aab80">
                Request your session
                <ArrowRightIcon className="h-5 w-5" data-id="element-99" data-mp="2z1cmeqc35s9e" />
              </a>
              <p className="mt-5 text-xs leading-5 text-white/40" data-id="element-100" data-mp="3czkhespkajhx">
                Testimonials reflect individual experiences and are not guarantees of results.
                This website does not provide medical advice.
              </p>
            </AnimatedSection>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 bg-[#0A1628] px-5 py-10 text-white sm:px-8" data-id="element-101" data-mp="enf77gnn4a3">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-102" data-mp="3iks3jjxsj93b">
          <div data-id="element-103" data-mp="3moq0bnzn87t7">
            <p className="font-serif text-2xl font-semibold" data-id="element-104" data-mp="38v8hk5es6z48">Dr. Ali Mazloum</p>
            <p className="mt-1 text-sm text-[#D4AF37]" data-id="element-105" data-mp="38t5hstnttrmb">The Human MRI · Co-Founder, Peptide Science Institute</p>
          </div>
          <p className="text-sm text-white/40" data-id="element-106" data-mp="2yxc8rk9bgg9a">© {new Date().getFullYear()} All rights reserved</p>
        </div>
      </footer>
    </div>;
}